import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card   from "@react/shared/ui/Card"
import Button from "@react/shared/ui/Button"

const formatAmount = (cents, symbol = "€") =>
  `${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`

const DISCOUNT_INPUT = "h-7 w-16 px-2 rounded-md border border-slate-200 text-[12px] text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition"

const SummaryPane = ({ totals, currencySymbol, discount, onDiscountChange, submitting, previewing, dirty, isEdit, generated, onPreview, onCreate, onGenerate }) => {
  const busy        = submitting || previewing
  const hasDiscount = (parseFloat(discount) || 0) > 0
  return (
  <div className="xl:sticky xl:top-4 space-y-4">
    <Card className="overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">
          {I18n.t("projects.invoices.builder.summary.eyebrow")}
        </div>
      </div>

      <dl className="px-5 py-4 space-y-2 text-[13px]">
        {hasDiscount && (
          <div className="flex items-center justify-between">
            <dt className="text-slate-600">{I18n.t("projects.invoices.builder.summary.gross_ht")}</dt>
            <dd className="tabular-nums text-slate-700">{formatAmount(totals.grossCents, currencySymbol)}</dd>
          </div>
        )}

        <div className="flex items-center justify-between">
          <dt className="text-slate-600 flex items-center gap-1.5">
            {I18n.t("projects.invoices.builder.summary.discount")}
            <input
              type      = "number"
              min       = "0"
              max       = "100"
              step      = "0.01"
              className = {DISCOUNT_INPUT}
              value     = {discount ?? ""}
              onChange  = {event => onDiscountChange(event.target.value)}
            />
            <span className="text-[11px] text-slate-400">%</span>
          </dt>
          <dd className="tabular-nums text-slate-700">{hasDiscount ? `- ${formatAmount(totals.discountCents, currencySymbol)}` : "—"}</dd>
        </div>

        <div className="flex items-center justify-between">
          <dt className="text-slate-600">{I18n.t("projects.invoices.builder.summary.total_ht")}</dt>
          <dd className="tabular-nums font-semibold text-slate-900">{formatAmount(totals.htCents, currencySymbol)}</dd>
        </div>

        <div className="border-t border-slate-100 my-2" />

        {totals.vatBreakdown.length === 0 ? (
          <div className="flex items-center justify-between">
            <dt className="text-slate-600">{I18n.t("projects.invoices.builder.summary.vat")}</dt>
            <dd className="tabular-nums text-slate-700">—</dd>
          </div>
        ) : (
          totals.vatBreakdown.map(entry => (
            <div key={entry.pct} className="flex items-center justify-between">
              <dt className="text-slate-600 flex items-center gap-1.5">
                {I18n.t("projects.invoices.builder.summary.vat")}
                <span className="text-[11px] text-slate-400 tabular-nums">{entry.pct}%</span>
              </dt>
              <dd className="tabular-nums text-slate-700">{formatAmount(entry.amountCents, currencySymbol)}</dd>
            </div>
          ))
        )}

        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <dt className="text-[14px] font-semibold">{I18n.t("projects.invoices.builder.summary.total_ttc")}</dt>
          <dd className="tabular-nums text-[16px] font-semibold text-slate-900">{formatAmount(totals.ttcCents, currencySymbol)}</dd>
        </div>
      </dl>

      <div className="px-5 pb-5 space-y-2">
        <Button variant="secondary" onClick={onPreview} disabled={busy} className="w-full justify-center">
          <FontAwesomeIcon icon={previewing ? "spinner" : "file-pdf"} spin={previewing} />
          {I18n.t("projects.invoices.builder.summary.preview")}
        </Button>
        <Button variant="secondary" onClick={onCreate} disabled={busy || (isEdit && !dirty)} className="w-full justify-center">
          <FontAwesomeIcon icon={submitting ? "spinner" : "floppy-disk"} spin={submitting} />
          {isEdit ? I18n.t("projects.invoices.builder.summary.save") : I18n.t("projects.invoices.builder.summary.create")}
        </Button>
        {!generated && (
          <Button variant="primary" onClick={onGenerate} disabled={busy} className="w-full justify-center">
            <FontAwesomeIcon icon={submitting ? "spinner" : "file-invoice"} spin={submitting} />
            {I18n.t("projects.invoices.builder.summary.generate")}
          </Button>
        )}
      </div>
    </Card>
  </div>
  )
}

export default SummaryPane
